import { useState } from "react";
import {
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROOM_CODE_LENGTH,
  normalizeRoomCode,
} from "../../convex/lib/game";
import { Bubbles, Mascot } from "../components/Ambient";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Footer } from "../components/Footer";
import { NavBar, NavWord } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { cx } from "../components/util";

type Mode = "choose" | "create" | "join";

export interface HomeProps {
  /** Prefilled from `?room=CODE` on a shared invite link. */
  initialCode?: string;
  initialName?: string;
  busy?: boolean;
  error?: string | null;
  onCreate: (name: string, soloTest?: boolean) => void;
  onJoin: (code: string, name: string) => void;
  /** Opens the account screen. Always optional — signing in never gates play. */
  onSignIn?: () => void;
  /** Display name of the signed-in account, if there is one. */
  account?: string | null;
  onSignOut?: () => void;
}

export function Home({
  initialCode = "",
  initialName = "",
  busy = false,
  error = null,
  onCreate,
  onJoin,
  onSignIn,
  account = null,
  onSignOut,
}: HomeProps) {
  const [mode, setMode] = useState<Mode>(initialCode ? "join" : "choose");
  const [name, setName] = useState(initialName);
  const [code, setCode] = useState(normalizeRoomCode(initialCode));
  const [soloTest, setSoloTest] = useState(false);

  const nameOk = name.trim().length >= 1;
  const codeOk = code.length === ROOM_CODE_LENGTH;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !nameOk) return;
    if (mode === "create") onCreate(name.trim(), soloTest);
    if (mode === "join" && codeOk) onJoin(code, name.trim());
  }

  return (
    <div className="bd-screen">
      <Bubbles />
      {/* Account lives in the nav and nowhere else. It is never in the create
          or join path — a party game that asks you to sign in before you can
          join a room is a party game people stop using. */}
      <NavBar
        actions={
          onSignIn ? (
            account ? (
              <NavWord onClick={() => onSignOut?.()}>{account}</NavWord>
            ) : (
              <NavWord onClick={onSignIn}>Sign in</NavWord>
            )
          ) : null
        }
      />

      <main className="shell">
        {/* Hero: the wordmark is the only place the display face runs this
            large, and it is always outlined — never rendered flat. */}
        <div className="plate chamfer bd-hero bd-slide-in">
          <Mascot />
          <h1 className="bd-wordmark t-outlined">
            <span>Badly</span>
            <span>Dubbed</span>
          </h1>
          <p className="t-hero-tagline bd-hero-tag">
            Everyone throws in a clip. You get someone else&rsquo;s. Dub it, badly,
            and hope the room votes for you.
          </p>
        </div>

        {/* One layout class only. `split` and `bd-stack-lg` both set `display`,
            and stacking them left the winner — and the children's sizing —
            down to stylesheet order. `bd-home-cols` adds no `display` of its
            own below 900px; above it, it takes over as a two-column grid. */}
        <div className="bd-stack-lg bd-home-cols">
          <div>
            <Panel
              title={mode === "join" ? "Join a room" : "Start a room"}
              aside={
                <span className="t-micro">
                  {MIN_PLAYERS}&ndash;{MAX_PLAYERS} players
                </span>
              }
            >
              <form onSubmit={submit} noValidate>
                {mode === "join" ? (
                  <Field
                    label="Room code"
                    code
                    value={code}
                    onChange={(e) => setCode(normalizeRoomCode(e.target.value))}
                    inputMode="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={ROOM_CODE_LENGTH}
                    placeholder="&mdash;&mdash;&mdash;&mdash;"
                    hint={`${ROOM_CODE_LENGTH} characters. No vowels, so it can't spell anything.`}
                    aria-label="Room code"
                  />
                ) : null}

                {mode !== "choose" ? (
                  <>
                    <Field
                      label="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value.slice(0, 16))}
                      maxLength={16}
                      autoComplete="nickname"
                      placeholder="Whatever the room will shout"
                      error={error}
                    />
                    {/* Solo test: lets one person exercise the record and
                        playback path without rounding up two friends. It
                        relaxes the player minimum, the never-your-own-clip
                        rule and the voting phase, so it is deliberately only
                        offered at creation and can never be switched on for a
                        room that already has people in it. */}
                    {mode === "create" ? (
                      <label className="row bd-solo-toggle">
                        <input
                          type="checkbox"
                          checked={soloTest}
                          onChange={(e) => setSoloTest(e.target.checked)}
                        />
                        <span className="grow">
                          <strong>Solo test room</strong>
                          <span className="t-micro muted bd-solo-note">
                            Play alone to try it out. You&rsquo;ll dub your own clip and
                            voting is skipped.
                          </span>
                        </span>
                      </label>
                    ) : null}

                    <div className="cluster">
                      <Button
                        variant="submit"
                        type="submit"
                        block
                        beacon={nameOk && (mode === "create" || codeOk)}
                        disabled={busy || !nameOk || (mode === "join" && !codeOk)}
                      >
                        {busy
                          ? "Working…"
                          : mode === "create"
                            ? soloTest
                              ? "Create solo room"
                              : "Create room"
                            : "Join room"}
                      </Button>
                    </div>
                    <hr className="dotted-divider" />
                    <Button variant="secondary" block onClick={() => setMode("choose")}>
                      Back
                    </Button>
                  </>
                ) : (
                  <div className="stack">
                    <Button variant="submit" block beacon onClick={() => setMode("create")}>
                      Create room
                    </Button>
                    <Button variant="primary" block onClick={() => setMode("join")}>
                      Join room
                    </Button>
                  </div>
                )}
              </form>
            </Panel>
          </div>

          <aside className={cx("panel", "bd-slide-in")}>
            <header className="panel-header">
              <span className="t-label">How it goes</span>
            </header>
            <div className="panel-body">
              <ol className="stack t-body bd-howto">
                <li className="row">Everyone uploads a clip with sound.</li>
                <li className="row">Clips get scrambled. You never get your own.</li>
                <li className="row">You watch, then re-voice it from memory.</li>
                <li className="row">All the dubs play back. Nobody is anonymous.</li>
                <li className="row">Rank your favourites. Points. Podium.</li>
              </ol>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
